import type { GameSession } from '../../game/simulation/GameSession';
import type { GameModeId } from '../../game/content/modes';
import type {
  PhysicsStepObservation,
  CardUsePreparation,
  CardUseRequest,
  PresentationState,
  SessionEvent,
  SessionResult,
} from '../../game/simulation/types';
import type { BodyFactory } from '../physics/BodyFactory';

export type SceneBridgeHooks = Readonly<{
  onResult: (result: SessionResult) => void;
  onPresentation: (presentation: PresentationState) => void;
}>;

export class SceneBridge {
  private readonly applied = new WeakSet<object>();
  private publishedState: SessionResult['state'] | null = null;

  public constructor(
    private readonly session: GameSession,
    private readonly bodies: BodyFactory,
    private readonly hooks: SceneBridgeHooks,
  ) {}

  public accept(result: SessionResult): void {
    if (this.applied.has(result)) throw new Error('A session result batch was applied twice');
    this.applied.add(result);
    this.bodies.setPhase(result.state.phase, result.state.mode);
    for (const command of result.commands) {
      switch (command.type) {
        case 'create-active-body':
          this.bodies.create(
            command.entityId,
            command.characterId,
            command.x,
            command.y,
            result.state.generation,
            false,
            command.initialVelocity,
          );
          break;
        case 'create-staged-body':
          this.bodies.create(
            command.entityId,
            command.characterId,
            command.x,
            command.y,
            result.state.generation,
            true,
          );
          break;
        case 'remove-body':
          this.bodies.remove(command.entityId);
          break;
        case 'activate-body':
          this.bodies.activate(command.entityId);
          break;
        case 'reposition-body':
          this.bodies.reposition(command.entityId, command.x, command.y);
          break;
        case 'replace-body':
          this.bodies.replace(command.entityId, command.characterId, result.state.generation, command.massMultiplier);
          break;
        case 'set-mass-multiplier':
          this.bodies.setMassMultiplier(command.entityId, command.multiplier);
          break;
        case 'zero-upward-velocity':
          this.bodies.zeroUpwardVelocity(command.entityId);
          break;
        case 'apply-wind':
          this.bodies.applyWind(command.entityId, command.direction);
          break;
      }
    }
    const stateChanged = this.publishedState !== result.state;
    if (stateChanged) this.publishedState = result.state;
    if (stateChanged || result.events.length > 0) this.hooks.onResult(result);
    const presentationEvent = result.events.find(
      (event): event is Extract<SessionEvent, { type: 'presentation-requested' }> =>
        event.type === 'presentation-requested',
    );
    if (presentationEvent) this.hooks.onPresentation(presentationEvent.presentation);
  }

  public start(): void {
    this.accept(this.session.start());
  }

  public beginDrop(x: number, y: number, step: number, nowMs: number, initialSpeed?: number): void {
    this.accept(this.session.beginDrop(x, y, step, nowMs, initialSpeed));
  }

  public physics(observation: PhysicsStepObservation): void {
    this.accept(this.session.applyPhysicsStep(observation));
  }

  public finishDrop(entityId: string): void {
    this.accept(this.session.finishDropWindow(entityId));
  }

  public confirmDrop(entityId: string, nowMs: number): void {
    this.accept(this.session.confirmDrop(entityId, nowMs));
  }

  public useCard(request: CardUseRequest, preparation: CardUsePreparation, nowMs: number): void {
    this.accept(this.session.useCard(request, preparation, nowMs));
  }

  public settlePresentation(id: number, outcome: 'success' | 'failure' | 'cancel'): void {
    this.accept(this.session.settlePresentation(id, outcome));
  }

  public pause(): void {
    this.accept(this.session.pause());
  }

  public resume(): void {
    this.accept(this.session.resume());
  }

  public restart(seed?: number, mode?: GameModeId, observerReached?: boolean): void {
    this.accept(this.session.restart(seed, mode, observerReached));
  }
}
